import { useCallback } from 'react'

import { Ok } from 'ts-results'

import { useApplicationStore } from 'app/application/infrastructure'
import { useApi } from 'app/common/application'

import {
  AccountsGetResponse,
  ConnectionEntity,
  ConnectionName,
  SupportedAccount,
  ValidateRequest,
  ValidateResponse,
} from '../types'

const getGetAccountsEndpointName = (connectionName: ConnectionName) => {
  switch (connectionName) {
    case 'criteo':
    case 'dv360':
      return 'advertisers'
    default:
      return 'accounts'
  }
}

const useConnectionsApi = (connectionName: ConnectionName) => {
  const { create, read } = useApi()

  const updateConnectionData = useApplicationStore((state) => state.updateConnection)

  const getAccounts = useCallback(async () => {
    const result = await read<AccountsGetResponse<SupportedAccount>>(
      `/client/v1/connections/${connectionName}/${getGetAccountsEndpointName(connectionName)}`,
    )
    if (result.ok) {
      return Ok(result.val.items)
    }

    return result
  }, [connectionName, read])

  const getOAuth = useCallback(async () => {
    const result = await read<ConnectionEntity>(`/client/v1/connections/${connectionName}/oauth`)
    if (result.ok) {
      updateConnectionData(result.val)
      return Ok(result.val)
    }

    return result
  }, [connectionName, read, updateConnectionData])

  const validateConnection = useCallback(
    async <V>(config: V) => {
      const result = await create<ValidateRequest<V>, ValidateResponse>(
        `/client/v1/connections/${connectionName}/validate`,
        config,
      )
      if (result.ok) {
        return Ok(result.val.valid)
      }

      return result
    },
    [connectionName, create],
  )

  return {
    getAccounts,
    getOAuth,
    validateConnection,
  }
}

export default useConnectionsApi
